import React, { useCallback, useContext, useMemo } from 'react';
import { DropDownArrow } from '@wix/wix-ui-icons-common/system';
import CloseButton, { CloseButtonSize } from '../../../CloseButton';
import StatusIndicator from '../../../StatusIndicator';
import { classes } from '../../Input.st.css';
import { dataHooks, SIZES } from '../../Input.constants';
import Tooltip from '../../../Tooltip';
import { InputProps, InputSize } from '../../Input.types';
import { StatusContext } from '../../../FormField/StatusContext';
import { useIcons } from '../../../WixDesignSystemIconThemeProvider';

export const Suffix = ({
  statusMessage,
  status,
  disabled,
  menuArrow,
  suffix,
  tooltipPlacement,
  clearButtonTooltipContent,
  clearButtonTooltipProps,
  clearButtonAriaLabel,
  statusMessageTooltipProps,
  hideStatusSuffix,
  onClear,
  clearButton,
  value,
  size,
  onInputClicked,
  focusOnClearClick,
  inputElementRef,
}: InputProps) => {
  const icons = useIcons('InputSuffix', {
    DropDownArrow,
  });

  const isClearButtonVisible =
    (!!onClear || !!clearButton) && !!value && !disabled;

  const statusContext = useContext(StatusContext);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!disabled) {
        inputElementRef.current?.focus();
        onInputClicked?.(event);
      }
    },
    [disabled, inputElementRef, onInputClicked],
  );

  const handleClear = useCallback(
    (
      event:
        | React.MouseEvent<HTMLButtonElement>
        | React.KeyboardEvent<HTMLButtonElement>,
    ) => {
      // Prevent the click from bubbling to an enclosing trigger (e.g. the
      // PopoverNext trigger in Dropdown/AutoComplete) which would toggle the
      // options open. Clearing the value must not open the dropdown.
      event.stopPropagation();
      if (focusOnClearClick) {
        inputElementRef.current?.focus();
      }
      onClear?.(event);
    },
    [focusOnClearClick, inputElementRef, onClear],
  );

  const ClearButton = useMemo(() => {
    if (!isClearButtonVisible) return null;

    const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleClear(event);
      }
    };

    const clearButtonSizeMap: Record<InputSize, CloseButtonSize> = {
      [SIZES.tiny]: 'small',
      [SIZES.small]: 'small',
      [SIZES.medium]: 'medium',
      [SIZES.large]: 'medium',
    };

    const ClearButtonElement = (
      <CloseButton
        as="button"
        className={classes.clearButton}
        dataHook={dataHooks.clearButton}
        skin="standardFilled"
        size={clearButtonSizeMap[size ?? SIZES.medium]}
        onClick={handleClear}
        onKeyDown={handleKeyDown}
        aria-label={clearButtonAriaLabel}
      />
    );

    return (
      <div className={classes.clearButtonWrapper}>
        {clearButtonTooltipContent ? (
          <Tooltip
            {...clearButtonTooltipProps}
            content={clearButtonTooltipContent}
            dataHook={dataHooks.tooltip}
          >
            {ClearButtonElement}
          </Tooltip>
        ) : (
          ClearButtonElement
        )}
      </div>
    );
  }, [
    isClearButtonVisible,
    handleClear,
    clearButtonTooltipContent,
    clearButtonTooltipProps,
    clearButtonAriaLabel,
    size,
  ]);

  const ErrorWarningIndicator = useMemo(() => {
    if (disabled || hideStatusSuffix) return null;
    if (status !== 'error' && status !== 'warning') return null;
    return (
      <div className={classes.statusWrapper}>
        <StatusIndicator
          dataHook={dataHooks.status}
          status={status}
          message={statusMessage}
          tooltipProps={{
            placement: tooltipPlacement,
            ...statusMessageTooltipProps,
          }}
        />
      </div>
    );
  }, [
    disabled,
    hideStatusSuffix,
    status,
    statusMessage,
    tooltipPlacement,
    statusMessageTooltipProps,
  ]);

  const LoadingIndicator = useMemo(() => {
    if (disabled || hideStatusSuffix) return null;
    if (statusContext.status !== 'loading' && status !== 'loading') return null;
    return (
      <div className={classes.statusWrapper}>
        <StatusIndicator
          dataHook={dataHooks.status}
          status={statusContext.status ?? status}
          message={statusMessage}
          tooltipProps={{
            placement: tooltipPlacement,
            ...statusMessageTooltipProps,
          }}
        />
      </div>
    );
  }, [
    disabled,
    hideStatusSuffix,
    statusMessage,
    tooltipPlacement,
    statusMessageTooltipProps,
    statusContext.status,
    status,
  ]);

  const CustomSuffix = useMemo(() => {
    if (!suffix) return null;
    return React.isValidElement(suffix) ? (
      React.cloneElement(suffix)
    ) : (
      <div className={classes.suffix}>{suffix}</div>
    );
  }, [suffix]);

  const MenuArrow = useMemo(() => {
    if (!menuArrow) return null;
    return (
      <div
        data-hook={dataHooks.menuArrow}
        className={classes.menuArrow}
        onClick={handleClick}
      >
        <icons.DropDownArrow />
      </div>
    );
  }, [menuArrow, handleClick, icons]);

  if (
    !ClearButton &&
    !ErrorWarningIndicator &&
    !LoadingIndicator &&
    !CustomSuffix &&
    !MenuArrow
  )
    return null;

  return (
    <div data-hook={dataHooks.suffixes} className={classes.suffixes}>
      {ClearButton}
      {ErrorWarningIndicator}
      {LoadingIndicator}
      {CustomSuffix}
      {MenuArrow}
    </div>
  );
};
